import { useCallback, useEffect, useMemo, useState } from 'react';

import config from '@/config';
import useStateHandler from '@/hooks/useStateHandler';
import { buildArtworkShareGatewayUrl, resolveAuctionSharePayload } from '@/pages/artwork/utils/share.utils';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';

import './styles/ArtworkSharePopup.css';

export default function ArtworkSharePopup({ open, onClose, artworkData, auctionData }) {
  const [copyLinkState, setCopyLinkState] = useState('idle');
  const [shareError, setShareError] = useState(null);
  const { state } = useStateHandler();
  const sharerUsername = state?.user_session?.user_username
    || state?.user_session?.username
    || null;
  const sharePayload = useMemo(
    () => resolveAuctionSharePayload({ artworkData, auctionData, sharerUsername }),
    [artworkData, auctionData, sharerUsername],
  );

  const {
    artworkTitle,
    artistName,
    shareSummary,
    previewDetails,
    shareUrl,
    shareImageUrl,
  } = sharePayload;
  const shareGatewayUrl = useMemo(
    () => buildArtworkShareGatewayUrl({
      artworkData,
      auctionData,
      apiBaseUrl: config.app.shareGatewayBaseUrl || config.apis.api.url,
      sharerUsername,
    }),
    [artworkData, auctionData, sharerUsername],
  );
  const shareableUrl = shareGatewayUrl || shareUrl;

  useEffect(() => {
    if (!open) {
      // Reset transient UI state so the popup feels fresh on the next open.
      setCopyLinkState('idle');
      setShareError(null);
    }
  }, [open]);

  const handleCopy = useCallback(async () => {
    if (!shareableUrl) {
      return;
    }
    console.debug('[ArtworkShare][desktop-popup-copy] url', shareableUrl);
    if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
      setShareError('Copy is not available on this device. Please copy the link manually.');
      setCopyLinkState('error');
      return;
    }
    try {
      await navigator.clipboard.writeText(shareableUrl);
      setShareError(null);
      setCopyLinkState('copied');
      setTimeout(() => setCopyLinkState('idle'), 2200);
    } catch (error) {
      console.error('[ArtworkSharePopup] Failed to copy text', error);
      setShareError('We could not copy the content. Please try again.');
      setCopyLinkState('error');
    }
  }, [shareableUrl]);

  if (!open) {
    return null;
  }

  return (
    <GenericPopup
      open
      className="auction-share-popup"
      backgroundClassName="auction-share-popup__backdrop"
      opacity
      onBackgroundClick={onClose}
    >
      <div className="auction-share-popup__header">
        <div>
          <h2 className="auction-share-popup__title">Share this auction</h2>
          <p className="auction-share-popup__subtitle">{shareSummary}</p>
        </div>
        <button
          type="button"
          className="auction-share-popup__close"
          onClick={onClose}
          aria-label="Close share popup"
        >
          <XIcon />
        </button>
      </div>

      <div className="auction-share-popup__preview">
        {shareImageUrl ? (
          <img
            src={shareImageUrl}
            alt={artworkTitle}
            className="auction-share-popup__preview-image"
          />
        ) : (
          <div className="auction-share-popup__preview-placeholder">No artwork image available</div>
        )}
        <div className="auction-share-popup__preview-meta">
          <span className="auction-share-popup__preview-tag">Suarte Auction</span>
          <h3 className="auction-share-popup__preview-title">{artworkTitle}</h3>
          {artistName && (
            <p className="auction-share-popup__preview-artist">By {artistName}</p>
          )}
          {previewDetails.length > 0 && (
            <p className="auction-share-popup__preview-details">
              {previewDetails.join(' | ')}
            </p>
          )}
        </div>
      </div>

      <div className="auction-share-popup__body">
        <div className="auction-share-popup__link-box">
          <div className="auction-share-popup__link-section">
            <label className="auction-share-popup__label" htmlFor="auction-share-link">
              Unique share link
            </label>
            <div className="auction-share-popup__link-row">
              <input
                id="auction-share-link"
                className="auction-share-popup__input"
                value={shareableUrl}
                readOnly
              />
              <button
                type="button"
                className={`auction-share-popup__copy ${copyLinkState === 'copied' ? 'auction-share-popup__copy--success' : ''}`}
                onClick={handleCopy}
              >
                {copyLinkState === 'copied' ? 'Copied' : 'Copy link'}
              </button>
            </div>
          </div>
        </div>

        {shareError && (
          <p className="auction-share-popup__error" role="alert">
            {shareError}
          </p>
        )}
      </div>
    </GenericPopup>
  );
}
